import React, { useEffect, useState, useContext } from 'react';
import { useParams, NavLink, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { HomeContext } from '../Context/HomeContext';
import { FaCalendarAlt, FaEye } from 'react-icons/fa';
import SubscribeBox from '../Component/Subscribe';
import './SubCategory.css';
import Loader from '../Component/Loader';

const SubCategoryPage = () => {
  const { slug } = useParams();
  const [blogs, setBlogs] = useState([]);
  const [subcategory, setSubcategory] = useState({});
  const [metaTags, setMetaTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const { homeData } = useContext(HomeContext);
  const recentBlogs = homeData?.recentBlogs || [];
  const categories = Array.isArray(homeData?.allCategories)
    ? homeData.allCategories
    : Object.values(homeData?.allCategories || {});

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}website/subCategoryBlogs/${slug}`,
          {
            headers: {
              Accept: 'application/json',
              'Client-Domain': process.env.REACT_APP_CLIENT_DOMAIN,
            },
          }
        );

        const record = res.data.record || {};
        setBlogs(res.data.blogs || []);
        setSubcategory(record);

        const metaHTML = record.meta_tags || '';
        const doc = new DOMParser().parseFromString(metaHTML, 'text/html');
        const tags = Array.from(doc.head.children);
        setMetaTags(tags);

        document.title = `${record.name || 'Subcategory'} - StreamPlay Network`;
      } catch (error) {
        console.error('Error fetching subcategory blogs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [slug]);

  const getMetaContent = (attr, value) => {
    const tag = metaTags.find((t) => t.getAttribute(attr) === value);
    return tag?.getAttribute('content') || '';
  };

  return (
    <>
      <Helmet>
        <title>{`${subcategory.name || 'Subcategory'} - StreamPlay Network`}</title>
        <meta name="title" content={getMetaContent('name', 'title')} />
        <meta name="description" content={getMetaContent('name', 'description')} />
        <meta name="keywords" content={getMetaContent('name', 'keywords')} />
        <meta property="og:title" content={getMetaContent('property', 'og:title')} />
        <meta property="og:description" content={getMetaContent('property', 'og:description')} />
        <meta property="og:image" content={getMetaContent('property', 'og:image')} />
      </Helmet>

      <section className="subcategory-hero">
        <h1 className="subcategory-heading">{subcategory.name}</h1>
        <p className="subcategory-sub">Explore top stories and updates from this subcategory.</p>
      </section>

      <div className="subcategory-grid-layout">
        <div className="subcategory-posts">
          {loading ? (
            <Loader text="Loading blogs..." />
          ) : blogs.length === 0 ? (
            <div className="empty-message">🚧 Content coming soon for this category.</div>
          ) : (

            blogs.map(blog => (
              <NavLink to={`/blog/${blog.slug}`} key={blog.id} className="blog-preview-card">
                <div className="preview-image-wrapper">
                  <img src={`https://api.funfillia.com/${blog.image_500x500}`} alt={blog.title} />
                </div>
                <div className="preview-content">
                  <h3>{blog.title}</h3>
                  <p>{blog.short_description}</p>
                  <div className="blog-meta">
                    <span><FaCalendarAlt /> {new Date(blog.published_at).toLocaleDateString()}</span>
                    &nbsp; &nbsp;<span><FaEye /> {blog.views_count}</span>
                  </div>
                </div>
              </NavLink>
            ))
          )}
        </div>

        <aside className="subcategory-sidebar">
          <h3 className="sidebar-title1">Recent Blogs</h3>
          {recentBlogs.map(item => (
            <NavLink to={`/blog/${item.slug}`} key={item.id} className="sidebar-blog-item">
              <img src={`https://api.funfillia.com/${item.image_200x200}`} alt={item.title} />
              <div className="sidebar-blog-text">
                <h4>{item.title}</h4>
                <div className="sidebar-meta">
                  <span><FaCalendarAlt /> {new Date(item.published_at).toLocaleDateString()}</span>
                  <span><FaEye /> {item.views_count}</span>
                </div>
              </div>
            </NavLink>
          ))}

          <div className="sidebar-section">
            <div className="sidebar-title">Categories</div>
            <ul className="sidebar-category-list">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link to={`/category/${cat.slug}`} className="category-link">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <SubscribeBox />
    </>
  );
};

export default SubCategoryPage;
